import { Suspense, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Trash2Icon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { getEffectiveUser } from '@modules/_shared/components/buyer-lead/buyerLeadMlsFallback';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import {
  UserCard,
  UserCardSkeleton,
} from '@modules/_shared/components/UserCard';
import { useLocale } from '@shared/hooks/locale';

type Props = {
  buyerLeadId?: string | null;
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
};

const GET_BUYER_LEAD_DETAIL = gql(/* GraphQL */ `
  query GetBuyerLeadDetail($id: uuid!) {
    buyer_leads_by_pk(id: $id) {
      id
      source
      created_at
      user {
        ...UserCard_user
      }
      # Cross-tenant MLS fallback: for buyer leads whose owner-tenant user
      # relationship is hidden by RLS (e.g. created by an agent on another
      # team), the user field resolves to null. Pull the display-safe
      # mls_user view so getEffectiveUser can render the card instead of
      # dereferencing null.
      mls_user {
        id
        first_name
        last_name
        full_name
        primary_email
        primary_phone_number
        profile_image_url
      }
    }
  }
`);

const BuyerLeadDetails: React.FC<{ buyerLeadId: string }> = ({
  buyerLeadId,
}) => {
  const { data } = useSuspenseQuery(GET_BUYER_LEAD_DETAIL, {
    variables: { id: buyerLeadId },
  });

  const lead = data.buyer_leads_by_pk;
  if (lead == null) {
    return null;
  }

  const secondaryText = (
    <>
      {lead.source != null ? `${lead.source}, ` : null}
      <TimeAgo dateString={lead.created_at} />
    </>
  );

  const effectiveUser = getEffectiveUser(lead);
  // No display-safe user (fully cross-tenant lead): skip the user card to
  // avoid a null deref in UserCard, but still show source + time so the
  // confirmation isn't blank.
  if (effectiveUser == null) {
    return (
      <Typography variant="body2" color="text.secondary">
        {secondaryText}
      </Typography>
    );
  }

  return (
    <UserCard user={effectiveUser} condensed secondaryText={secondaryText} />
  );
};

export const BuyerLeadDeleteDialog = (props: Props) => {
  const { buyerLeadId, open, onClose, onDelete } = props;
  const { t } = useLocale();
  const [errorLike, setErrorLike] = useState<ErrorLike | null>(null);

  const [deleteBuyerLead, { loading: deleting }] = useMutation(
    gql(/* GraphQL */ `
      mutation BuyerLeadDeleteDialog($id: uuid!) {
        delete_buyer_leads_by_pk(id: $id) {
          id
        }
      }
    `),
    {
      update: (cache, { data }) => {
        if (data?.delete_buyer_leads_by_pk == null) {
          return;
        }

        const id = cache.identify({
          __typename: 'buyer_leads',
          id: buyerLeadId,
        });

        cache.evict({ id });
      },
      onError: setErrorLike,
    },
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        root: {
          'data-no-dnd': true,
        } as any,
      }}
    >
      <DialogTitle>
        {t('Are you sure you want to delete this lead?')}
      </DialogTitle>
      <DialogContent>
        {buyerLeadId && (
          <Suspense fallback={<UserCardSkeleton condensed hasSecondaryText />}>
            <BuyerLeadDetails buyerLeadId={buyerLeadId} />
          </Suspense>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <LoadingButton
          color="error"
          variant="contained"
          startIcon={<Trash2Icon />}
          loading={deleting}
          loadingIndicator={
            <CircularProgress disableShrink color="inherit" size={16} />
          }
          onClick={() => {
            if (buyerLeadId == null) {
              return;
            }

            deleteBuyerLead({
              variables: { id: buyerLeadId },
              onCompleted: onDelete,
            });
          }}
        >
          {t('Delete')}
        </LoadingButton>
      </DialogActions>
      <MutationErrorSnackbar
        error={errorLike}
        onClose={() => setErrorLike(null)}
      />
    </Dialog>
  );
};
